import { useEffect } from 'react';

/**
 * Filter persistence for admin list/detail pages.
 *
 * Admin "Back" navigation is a hard <Link> that fully remounts the list, so
 * component-local useState filters are lost on return. These helpers snapshot a
 * page's filters into sessionStorage so the exact same view is restored when the
 * user comes back (browser Back or an in-app remount). sessionStorage — not the
 * URL — because some detail links open in a new tab (no history entry to
 * traverse) and we want zero routing changes.
 *
 * Usage per page:
 *   const STATE_KEY = "myPageState";
 *   // in the mount effect, before applying defaults:
 *   const saved = readSavedFilters(STATE_KEY);
 *   if (saved) { ...apply saved... } else { ...apply defaults... }
 *   // once, at the top level of the component:
 *   usePersistFilters(STATE_KEY, { fromDate, toDate, searchValue, page, ... });
 */

/**
 * Map of a list page's route → its filter-snapshot key. Used to clear a page's saved
 * filters when the user re-enters it FRESH from the sidebar menu (see clearPageFilters).
 *
 * Why the sidebar (not navigation type): the in-app "← Back" affordance is itself a hard
 * <Link> (a history PUSH), so it is indistinguishable from a sidebar click by navigation
 * type. Clicking a sidebar item is therefore the only reliable "start fresh" signal. We
 * clear ONLY the destination page's key so returning (Back) to a DIFFERENT page still
 * restores that page's filters.
 */
export const PAGE_FILTER_KEYS = {
  '/users': 'usersListState',
  '/user-heart-rate-sessions': 'heartRateSessionsListState',
  '/user-pause-count': 'pauseListState',
  '/continuous-usage-streaks': 'continuousUsageStreaksState',
  '/pause-alert-impact': 'pauseAlertImpactListState',
  '/user-monitor-sessions': 'monitorSessionListState',
  '/user-subscription': 'subscriptionListState',
  '/user-bio-vitals': 'bioVitalsListState',
  '/user-medicine': 'medicineListState',
  '/user-sleep-sessions': 'sleepSessionsState',
  '/user-sleep-summary': 'sleepSummaryListState',
  '/standing-check-sessions': 'standingCheckSessionListState',
  '/workout-sessions': 'workoutSessionListState',
  '/workout-records': 'workoutRecordListState',
  '/report-history': 'reportHistoryState',
  '/event-logs': 'eventLogsState',
  '/bigquery-install-events': 'bigQueryInstallEventsState',
  // User Feedback (067) is four tabs on one screen, each with its own filters. Only the ACTIVE
  // tab's key can be cleared from here, so the entry points at the tab that opens by default;
  // the other three keep their filters until their own tab is entered fresh.
  // (The Survey Responses tab stores under 'surveyResponsesState' — it needs a snapshot because
  //  opening a response is a real navigation to /survey-response/:id, not a dialog.)
  '/user-feedback': 'firebaseFeedbacksState',
};

/**
 * Clear the saved filter snapshot for a page, given its path (or a full href/URL).
 * No-op for paths without a registered filter key. Best-effort (storage may be blocked).
 */
export function clearPageFilters(pathOrHref) {
  if (!pathOrHref) return;
  const path = String(pathOrHref).split('?')[0].split('#')[0];
  const key = PAGE_FILTER_KEYS[path];
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable — best-effort */
  }
}

/** Read a previously-saved filter snapshot for `key`, or null if none/invalid. */
export function readSavedFilters(key) {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

/** Persist `snapshot` under `key` whenever it changes. */
export function usePersistFilters(key, snapshot) {
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(snapshot));
    } catch {
      /* storage unavailable (private mode / quota) — persistence is best-effort */
    }
    // Re-serialise as the dependency so nested value changes are detected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, JSON.stringify(snapshot)]);
}
